/**
 * Ably Realtime client for customer portal.
 * Backend has migrated from Socket.IO to Ably; use this client for all real-time subscriptions.
 * See docs/ABLY_CUSTOMER_PORTAL.md for channels and events.
 */
import Ably from 'ably';
import { apiService } from './api';

let realtimeInstance: Ably.Realtime | null = null;

function getTokenRequest(): Promise<any> {
  return apiService.getAblyToken().then((res: any) => {
    const data = res?.data;
    if (data != null && (data.keyName != null || data.ttl != null)) return data;
    if (res?.keyName != null || res?.ttl != null) return res;
    throw new Error(res?.message || 'Ably token failed');
  });
}

/**
 * Get or create the singleton Ably Realtime client (authCallback with customer JWT).
 * Call after customer is logged in.
 */
export function getAblyRealtime(): Ably.Realtime {
  if (realtimeInstance) return realtimeInstance;

  realtimeInstance = new Ably.Realtime({
    authCallback: (tokenParams, callback) => {
      getTokenRequest()
        .then((tokenRequest) => callback(null, tokenRequest))
        .catch((err) => callback(err, null));
    },
  });

  realtimeInstance.connection.on('connected', () => {
    console.log('[Ably] Connected');
  });
  realtimeInstance.connection.on('failed', (state) => {
    // Realtime is auxiliary: a failed Ably connection (e.g. the backend's ably-token endpoint is
    // returning 503 because ABLY_API_KEY is unset, or a transient network error) must NOT destroy
    // the customer's session. Chat/history still work over REST; we simply lose live updates.
    // Previously this cleared tokens + redirected to /login whenever the failure reason contained
    // the word "token" — and the backend's "Real-time token unavailable" message matched that,
    // so a server-side Ably misconfig logged every customer out the moment chat opened.
    console.warn('[Ably] Connection failed (live updates disabled; chat still works over REST):', state?.reason);
  });
  realtimeInstance.connection.on('disconnected', () => {
    console.log('[Ably] Disconnected');
  });

  return realtimeInstance;
}

/**
 * True only when the underlying Ably connection is actually live right now. Use this (not the mere
 * presence of a subscription) to decide whether a poll fallback should run — during a `suspended`
 * or `disconnected` gap this returns false so the safety-net poll kicks in.
 */
export function isAblyConnected(): boolean {
  return realtimeInstance?.connection?.state === 'connected';
}

/**
 * Disconnect and clear the singleton (e.g. on logout).
 */
export function disconnectAbly(): void {
  if (realtimeInstance) {
    realtimeInstance.close();
    realtimeInstance = null;
    console.log('[Ably] Closed');
  }
}

/**
 * Subscribe to a channel and forward events to handlers (same event names as before).
 * Returns an unsubscribe function.
 */
export function subscribeToChannel(
  channelName: string,
  events: readonly string[],
  emit: (event: string, data: any) => void
): () => void {
  const client = getAblyRealtime();
  // Reconnect recovery: attach with a short `rewind` so events published during a brief disconnect
  // are replayed on (re)attach — otherwise a dropped connection silently loses tickets/orders/messages.
  // Every consumer dedupes by id, so replays are safe. All callers go through THIS function, so the
  // rewind params are applied consistently on the shared channel object returned by channels.get().
  // If mixing params on an already-attached shared channel ever throws, fall back to a plain get()
  // so realtime keeps working (Ably remains auxiliary — a failure here must never be fatal).
  let channel: ReturnType<typeof client.channels.get>;
  try {
    channel = client.channels.get(channelName, { params: { rewind: '15s' } });
  } catch {
    channel = client.channels.get(channelName);
  }
  const subscriptions: Array<{ event: string; handler: (msg: Ably.Message) => void }> = [];

  events.forEach((event) => {
    const handler = (msg: Ably.Message) => {
      const data = msg.data;
      emit(event, data);
    };
    channel.subscribe(event, handler);
    subscriptions.push({ event, handler });
  });

  return () => {
    // Only remove THIS subscription's handlers. Do NOT detach the channel: channels.get() returns a
    // shared channel object, so detaching would kill every other component subscribed to the same
    // channel (e.g. closing the order popup was detaching user:{id} and silencing the table's live
    // notifications). Ably keeps the channel; the whole client is closed on logout (disconnectAbly).
    subscriptions.forEach(({ event, handler }) => channel.unsubscribe(event, handler));
  };
}
